/**
 * 학력 관련 유틸리티 함수
 *
 * 최종학력 코드와 근속 보정연수 간의 매핑을 관리합니다.
 */

import { EducationLevel } from '../types/employee';

/**
 * 학력 정보
 */
export interface EducationInfo {
  /** 표시명 (한글) */
  label: string;
  /** 근속 보정연수 (년 단위) */
  years: number;
}

/**
 * 최종학력 옵션 목록
 */
export const EDUCATION_LEVEL_OPTIONS: Array<{
  value: EducationLevel;
  label: string;
  years: number;
}> = [
  { value: 'HIGH_SCHOOL', label: '고졸', years: 0 },
  { value: 'COLLEGE_2Y', label: '초대졸2년', years: 2 },
  { value: 'COLLEGE_3Y', label: '초대졸3년', years: 3 },
  { value: 'UNIVERSITY', label: '대졸', years: 4 },
  { value: 'GRADUATE', label: '대학원졸', years: 6 },
];

/**
 * 최종학력 코드로부터 학력 정보를 반환합니다.
 *
 * @param level 최종학력 코드
 * @returns 학력 정보 (표시명, 근속 보정연수)
 *
 * @example
 * ```typescript
 * const info = getEducationInfo('UNIVERSITY');
 * console.log(info); // { label: '대졸', years: 4 }
 * ```
 */
export function getEducationInfo(
  level: EducationLevel | undefined | null
): EducationInfo | null {
  if (!level) {
    return null;
  }

  switch (level) {
    case 'HIGH_SCHOOL':
      return { label: '고졸', years: 0 };
    case 'COLLEGE_2Y':
      return { label: '초대졸2년', years: 2 };
    case 'COLLEGE_3Y':
      return { label: '초대졸3년', years: 3 };
    case 'UNIVERSITY':
      return { label: '대졸', years: 4 };
    case 'GRADUATE':
      return { label: '대학원졸', years: 6 };
    default:
      return null;
  }
}

/**
 * 최종학력 표시명으로부터 학력 코드를 반환합니다.
 * (엑셀 업로드 등에서 사용)
 *
 * @param label 최종학력 표시명 (예: "고졸", "대졸")
 * @returns 최종학력 코드
 *
 * @example
 * ```typescript
 * const level = getEducationLevelFromLabel('대졸');
 * console.log(level); // 'UNIVERSITY'
 * ```
 */
export function getEducationLevelFromLabel(
  label: string | undefined | null
): EducationLevel | null {
  if (!label) {
    return null;
  }

  const trimmed = label.trim();

  switch (trimmed) {
    case '고졸':
      return 'HIGH_SCHOOL';
    case '초대졸2년':
    case '전문대졸(2년)':
    case '전문학사(2년)':
      return 'COLLEGE_2Y';
    case '초대졸3년':
    case '전문대졸(3년)':
    case '전문학사(3년)':
      return 'COLLEGE_3Y';
    case '대졸':
    case '학사':
    case '학사(4년)':
      return 'UNIVERSITY';
    case '대학원졸':
    case '대학원':
    case '석사':
      return 'GRADUATE';
    default:
      return null;
  }
}

/**
 * 학력 코드와 전공으로부터 저장할 데이터를 생성합니다.
 *
 * @param educationLevel 최종학력 코드
 * @param major 전공 (선택)
 * @returns 저장용 데이터 (educationLevel, educationLevelLabel, educationYears, major)
 *
 * @example
 * ```typescript
 * const data = buildEducationData('UNIVERSITY', '컴퓨터공학과');
 * console.log(data);
 * // {
 * //   educationLevel: 'UNIVERSITY',
 * //   educationLevelLabel: '대졸',
 * //   educationYears: 4,
 * //   major: '컴퓨터공학과'
 * // }
 * ```
 */
export function buildEducationData(
  educationLevel: EducationLevel | undefined | null,
  major?: string
): {
  educationLevel?: EducationLevel;
  educationLevelLabel?: string;
  educationYears?: number;
  major?: string;
} {
  if (!educationLevel) {
    return {
      educationLevel: undefined,
      educationLevelLabel: undefined,
      educationYears: undefined,
      major: major || undefined,
    };
  }

  const info = getEducationInfo(educationLevel);

  return {
    educationLevel,
    educationLevelLabel: info?.label,
    educationYears: info?.years,
    major: major || undefined,
  };
}

/**
 * 레거시 educationLevel 코드를 표준 코드로 정규화
 * (EducationTab.tsx와 동일한 로직)
 *
 * - ASSOCIATE_2 → COLLEGE_2Y
 * - ASSOCIATE_3 → COLLEGE_3Y
 * - BACHELOR → UNIVERSITY
 */
const LEGACY_EDUCATION_MAPPING: Record<string, EducationLevel> = {
  'ASSOCIATE_2': 'COLLEGE_2Y',
  'ASSOCIATE_3': 'COLLEGE_3Y',
  'BACHELOR': 'UNIVERSITY',
  'MASTER': 'GRADUATE',
  'HIGH_SCHOOL': 'HIGH_SCHOOL',
  'COLLEGE_2Y': 'COLLEGE_2Y',
  'COLLEGE_3Y': 'COLLEGE_3Y',
  'UNIVERSITY': 'UNIVERSITY',
  'GRADUATE': 'GRADUATE',
};

export function normalizeEducationLevel(value: string | undefined | null): EducationLevel | null {
  if (!value) return null;
  return LEGACY_EDUCATION_MAPPING[value] || null;
}

/**
 * 학력 코드(레거시 포함)를 한글 라벨로 변환
 * (EducationTab.tsx와 동일한 출력 형식)
 *
 * @example
 * getEducationLevelLabelSafe('ASSOCIATE_2') // '초대졸(2년)'
 * getEducationLevelLabelSafe('BACHELOR')    // '대졸'
 */
const EDUCATION_DISPLAY_LABELS: Record<EducationLevel, string> = {
  'HIGH_SCHOOL': '고졸',
  'COLLEGE_2Y': '초대졸(2년)',
  'COLLEGE_3Y': '초대졸(3년)',
  'UNIVERSITY': '대졸',
  'GRADUATE': '대학원졸',
};

export function getEducationLevelLabelSafe(value: string | undefined | null): string {
  if (!value) return '-';

  const normalized = normalizeEducationLevel(value);
  if (!normalized) return '-';

  return EDUCATION_DISPLAY_LABELS[normalized] || '-';
}
